import React, { useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { updateUserProfile } from "../../../utils/userApi";

const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const AGE_GROUP_OPTIONS = [
  "Children (6-12)",
  "Teens (13-17)",
  "Young Adults (18-25)",
  "Adults (25+)",
];

const mergeOpeningHours = (existing = []) =>
  DAYS.map(
    (day) =>
      existing.find((d) => d.day === day) || {
        day,
        open: "",
        close: "",
        closed: false,
      },
  );

const boxStyle = {
  border: "1px solid #e2e8f0",
  borderRadius: "8px",
  padding: "14px",
  marginBottom: "14px",
};

const removeBtnStyle = {
  background: "none",
  border: "none",
  color: "#e24b4a",
  cursor: "pointer",
  fontSize: "13px",
  fontWeight: 600,
};

const addBtnStyle = {
  background: "none",
  border: "1px dashed #00b8ff",
  color: "#0080b8",
  borderRadius: "6px",
  padding: "8px 14px",
  cursor: "pointer",
  fontSize: "13px",
  fontWeight: 600,
};

const InstitutionDetailsForm = ({ onCancel, setStatusModal }) => {
  const { user, updateUser } = useAuth();
  const [isSaving, setIsSaving] = useState(false);

  const [facilitiesText, setFacilitiesText] = useState(
    (user?.facilities || []).join("\n"),
  );
  const [accreditationsText, setAccreditationsText] = useState(
    (user?.accreditations || []).join("\n"),
  );
  const [ageGroups, setAgeGroups] = useState(user?.ageGroups || []);
  const [openingHours, setOpeningHours] = useState(
    mergeOpeningHours(user?.openingHours),
  );
  const [classSchedules, setClassSchedules] = useState(
    user?.classSchedules || [],
  );
  const [branches, setBranches] = useState(user?.branches || []);
  const [upcomingProgrammes, setUpcomingProgrammes] = useState(
    (user?.upcomingProgrammes || []).map((p) => ({
      ...p,
      startDate: p.startDate ? new Date(p.startDate).toISOString().slice(0, 10) : "",
    })),
  );
  const [newPhotos, setNewPhotos] = useState([]);
  const existingPhotoCount = user?.photos?.length || 0;

  const toggleAgeGroup = (label) => {
    setAgeGroups((prev) =>
      prev.includes(label) ? prev.filter((a) => a !== label) : [...prev, label],
    );
  };

  const updateOpeningHoursDay = (day, field, value) => {
    setOpeningHours((prev) =>
      prev.map((d) => (d.day === day ? { ...d, [field]: value } : d)),
    );
  };

  const addClassSchedule = () =>
    setClassSchedules((prev) => [...prev, { title: "", days: "", time: "" }]);
  const updateClassSchedule = (index, field, value) =>
    setClassSchedules((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );
  const removeClassSchedule = (index) =>
    setClassSchedules((prev) => prev.filter((_, i) => i !== index));

  const addBranch = () =>
    setBranches((prev) => [...prev, { name: "", address: "" }]);
  const updateBranch = (index, field, value) =>
    setBranches((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );
  const removeBranch = (index) =>
    setBranches((prev) => prev.filter((_, i) => i !== index));

  const addProgramme = () =>
    setUpcomingProgrammes((prev) => [
      ...prev,
      { title: "", startDate: "", description: "" },
    ]);
  const updateProgramme = (index, field, value) =>
    setUpcomingProgrammes((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );
  const removeProgramme = (index) =>
    setUpcomingProgrammes((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setIsSaving(true);

      const payload = {
        facilities: facilitiesText
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
        accreditations: accreditationsText
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
        ageGroups,
        openingHours,
        classSchedules: classSchedules.filter((r) => r.title.trim()),
        branches: branches.filter((r) => r.name.trim()),
        upcomingProgrammes: upcomingProgrammes
          .filter((r) => r.title.trim())
          .map((r) => ({ ...r, startDate: r.startDate || null })),
        ...(newPhotos.length > 0 && { photos: newPhotos }),
      };

      const res = await updateUserProfile(payload);
      updateUser(res.user);

      setStatusModal({
        open: true,
        type: "success",
        message: "Training Centre Details Updated",
        subtext: "Your facilities, schedule, and programme details have been saved.",
      });

      onCancel();
    } catch (err) {
      console.error(err);
      setStatusModal({
        open: true,
        type: "error",
        message: "Error Updating Details",
        subtext: "There was an error saving your training centre details. Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="edit-form-card">
      <div className="form-group">
        <label>Facilities (one per line)</label>
        <textarea
          rows={4}
          value={facilitiesText}
          onChange={(e) => setFacilitiesText(e.target.value)}
          placeholder={"e.g. Air-conditioned classrooms\nComputer lab\nLibrary"}
        />
      </div>

      <div className="form-group">
        <label>Centre Photos</label>
        <p style={{ fontSize: "13px", color: "#64748b", margin: "0 0 8px" }}>
          {existingPhotoCount} photo{existingPhotoCount === 1 ? "" : "s"} uploaded so far.
        </p>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          onChange={(e) => setNewPhotos(Array.from(e.target.files || []))}
        />
        {newPhotos.length > 0 && (
          <p style={{ fontSize: "12px", color: "#0080b8", marginTop: "6px" }}>
            {newPhotos.length} new photo{newPhotos.length === 1 ? "" : "s"} selected — will be added on save.
          </p>
        )}
      </div>

      <div className="form-group">
        <label>Learner Age Groups</label>
        <div className="checkbox-group">
          {AGE_GROUP_OPTIONS.map((label) => (
            <label key={label} className="checkbox-label">
              <input
                type="checkbox"
                checked={ageGroups.includes(label)}
                onChange={() => toggleAgeGroup(label)}
              />
              {label}
            </label>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label>Accreditations / Affiliations (one per line)</label>
        <textarea
          rows={3}
          value={accreditationsText}
          onChange={(e) => setAccreditationsText(e.target.value)}
          placeholder={"e.g. NABTEB Accredited\nNigerian Educational Research and Development Council"}
        />
      </div>

      <div className="form-group">
        <label>Opening Hours</label>
        {openingHours.map((d) => (
          <div
            key={d.day}
            style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}
          >
            <span style={{ width: "90px", fontSize: "13px" }}>{d.day}</span>
            <label style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px" }}>
              <input
                type="checkbox"
                checked={d.closed}
                onChange={(e) => updateOpeningHoursDay(d.day, "closed", e.target.checked)}
              />
              Closed
            </label>
            {!d.closed && (
              <>
                <input
                  type="time"
                  value={d.open}
                  onChange={(e) => updateOpeningHoursDay(d.day, "open", e.target.value)}
                />
                <span>to</span>
                <input
                  type="time"
                  value={d.close}
                  onChange={(e) => updateOpeningHoursDay(d.day, "close", e.target.value)}
                />
              </>
            )}
          </div>
        ))}
      </div>

      <div className="form-group">
        <label>Class Schedules</label>
        {classSchedules.map((row, i) => (
          <div key={i} style={boxStyle}>
            <input
              type="text"
              placeholder="Programme (e.g. JAMB Weekday Class)"
              value={row.title}
              onChange={(e) => updateClassSchedule(i, "title", e.target.value)}
              style={{ marginBottom: "8px", width: "100%", boxSizing: "border-box" }}
            />
            <input
              type="text"
              placeholder="Days (e.g. Mon, Wed, Fri)"
              value={row.days}
              onChange={(e) => updateClassSchedule(i, "days", e.target.value)}
              style={{ marginBottom: "8px", width: "100%", boxSizing: "border-box" }}
            />
            <input
              type="text"
              placeholder="Time (e.g. 4:00pm - 6:00pm)"
              value={row.time}
              onChange={(e) => updateClassSchedule(i, "time", e.target.value)}
              style={{ marginBottom: "8px", width: "100%", boxSizing: "border-box" }}
            />
            <button type="button" style={removeBtnStyle} onClick={() => removeClassSchedule(i)}>
              Remove
            </button>
          </div>
        ))}
        <button type="button" style={addBtnStyle} onClick={addClassSchedule}>
          + Add Class Schedule
        </button>
      </div>

      <div className="form-group">
        <label>Branches</label>
        {branches.map((row, i) => (
          <div key={i} style={boxStyle}>
            <input
              type="text"
              placeholder="Branch name"
              value={row.name}
              onChange={(e) => updateBranch(i, "name", e.target.value)}
              style={{ marginBottom: "8px", width: "100%", boxSizing: "border-box" }}
            />
            <input
              type="text"
              placeholder="Branch address"
              value={row.address}
              onChange={(e) => updateBranch(i, "address", e.target.value)}
              style={{ marginBottom: "8px", width: "100%", boxSizing: "border-box" }}
            />
            <button type="button" style={removeBtnStyle} onClick={() => removeBranch(i)}>
              Remove
            </button>
          </div>
        ))}
        <button type="button" style={addBtnStyle} onClick={addBranch}>
          + Add Branch
        </button>
      </div>

      <div className="form-group">
        <label>Upcoming Programmes</label>
        {upcomingProgrammes.map((row, i) => (
          <div key={i} style={boxStyle}>
            <input
              type="text"
              placeholder="Programme title"
              value={row.title}
              onChange={(e) => updateProgramme(i, "title", e.target.value)}
              style={{ marginBottom: "8px", width: "100%", boxSizing: "border-box" }}
            />
            <input
              type="date"
              value={row.startDate}
              onChange={(e) => updateProgramme(i, "startDate", e.target.value)}
              style={{ marginBottom: "8px", width: "100%", boxSizing: "border-box" }}
            />
            <textarea
              rows={2}
              placeholder="Short description (optional)"
              value={row.description}
              onChange={(e) => updateProgramme(i, "description", e.target.value)}
              style={{ marginBottom: "8px", width: "100%", boxSizing: "border-box" }}
            />
            <button type="button" style={removeBtnStyle} onClick={() => removeProgramme(i)}>
              Remove
            </button>
          </div>
        ))}
        <button type="button" style={addBtnStyle} onClick={addProgramme}>
          + Add Upcoming Programme
        </button>
      </div>

      <div className="form-actions">
        <button type="button" className="edit-cancel-btn" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="edit-primary-btn" disabled={isSaving}>
          {isSaving ? "Saving..." : "Save"}
        </button>
      </div>
    </form>
  );
};

export default InstitutionDetailsForm;
